import { useQuery } from "@apollo/client/react";
import  { useContext, useEffect, useMemo } from 'react'
import { GET_SETTINGS, GET_PREFERENCES, GET_NOTIFICATIONS } from './queries'
import {
  SettingsResponse,
  GetPreferencesResponse,
  GetNotificationsResponse,
} from './types'
import { isMfaPending, StateStore } from '../StoreProvider'
import {
  storeBackendNotifications,
  setBackendNotificationsFetched,
  backendNotificationsFetched,
} from '../../graphql-client/storedState'
import { getStationFilters } from './station'

export const useSettings = () => {
  const { state: { currentUser } } = useContext(StateStore)

  const { data } = useQuery<SettingsResponse>(GET_SETTINGS, {
    skip: !currentUser || isMfaPending(currentUser),
    fetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
  })

  return data?.settings.__typename === 'GetSettingsSuccess' ? data.settings.settings : null
}

export const usePreferences = () => {
  const { state: { currentUser } } = useContext(StateStore)
  const settings = useSettings()

  const { data, loading } = useQuery<GetPreferencesResponse>(GET_PREFERENCES, {
    skip: !currentUser || isMfaPending(currentUser),
    fetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
  })

  const preferences = useMemo(() => {
    if (!data) return null
    if (data.preferences.__typename !== 'GetPreferencesSuccess') return null

    const { id, filters } = data.preferences.preferences
    const parsed = filters ? JSON.parse(filters) : null
    const serverPreferences = { id, filters: parsed }

    return getStationFilters(settings, currentUser?.user_id ?? null, serverPreferences)
  }, [data, settings, currentUser?.user_id])

  return { preferences, loading }
}

export const useNotifications = () => {
  const { state: { currentUser } } = useContext(StateStore)

  const { data } = useQuery<GetNotificationsResponse>(GET_NOTIFICATIONS, {
    skip: !currentUser || isMfaPending(currentUser) || backendNotificationsFetched(),
    fetchPolicy: 'cache-and-network',
  })

  useEffect(() => {
    if (data?.notifications && data.notifications.__typename === 'GetNotificationsSuccess') {
      storeBackendNotifications(data.notifications.notifications)
      setBackendNotificationsFetched()
      window.dispatchEvent(new Event('backend-notifications-updated'))
    }
  }, [data?.notifications])
}